import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Library, Mail, LogOut, Loader2 } from "lucide-react";
import api, { API, customerAuth, fileUrl } from "../lib/api";
import { ReviewForm } from "../components/ReviewForm";
import { DownloadButtons } from "../components/DownloadButtons";
import { useSeo } from "../lib/seo";

export default function LibraryPage() {
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState({});
  useSeo({ title: "My Library", description: "Sign in with your email to re-download every Faithful Store purchase, in every format, anytime.", path: "/library", noindex: true });
  const [state, setState] = useState(localStorage.getItem("fs_customer_token") ? "loading" : "anon");

  const loadOrders = async () => {
    try {
      const [r, rv] = await Promise.all([api.get("/library/orders", customerAuth()), api.get("/library/reviews", customerAuth())]);
      setData(r.data); setReviews(Object.fromEntries(rv.data.map((x) => [x.product_id, x]))); setState("ready");
    }
    catch { localStorage.removeItem("fs_customer_token"); setState("anon"); }
  };

  useEffect(() => {
    const token = params.get("token");
    if (token) {
      setState("verifying");
      api.post("/library/verify", { token })
        .then((r) => { localStorage.setItem("fs_customer_token", r.data.access_token); setParams({}); loadOrders(); })
        .catch((e) => { toast.error(e?.response?.data?.detail || "Link invalid"); setParams({}); setState("anon"); });
    } else if (state === "loading") loadOrders();
  }, []); // eslint-disable-line

  const request = async (e) => {
    e.preventDefault();
    await api.post("/library/request", { email });
    setSent(true);
  };
  const signOut = () => { localStorage.removeItem("fs_customer_token"); setData(null); setState("anon"); setSent(false); };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="library-page">
      <div className="overline text-[#d4af37] flex items-center gap-2"><Library className="w-4 h-4" /> Your library</div>
      <h1 className="font-serif text-5xl mt-3 text-[#0a192f] tracking-tight">Every purchase, always here.</h1>

      {(state === "anon") && (
        <div className="card-flat p-10 mt-12 max-w-xl" data-testid="library-login">
          {!sent ? (
            <form onSubmit={request}>
              <p className="text-[#4a4a4a]">Enter the email you used at checkout. We'll send a one-time sign-in link — no password needed.</p>
              <input data-testid="library-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-6 w-full border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#0a192f]" />
              <button data-testid="library-request-btn" className="btn-navy mt-4 inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Email me a sign-in link</button>
            </form>
          ) : (
            <div data-testid="library-sent">
              <Mail className="w-8 h-8 text-[#d4af37]" />
              <h2 className="font-serif text-2xl text-[#0a192f] mt-4">Check your inbox</h2>
              <p className="text-[#4a4a4a] mt-2">If <strong>{email}</strong> has an account with us, a sign-in link is on its way. It works once and expires in 15 minutes.</p>
              <button onClick={() => setSent(false)} className="text-sm font-semibold text-[#0a192f] hover:text-[#d4af37] mt-5">Use a different email</button>
            </div>
          )}
        </div>
      )}

      {(state === "verifying" || state === "loading") && <div className="mt-12 flex items-center gap-3 text-[#4a4a4a]"><Loader2 className="w-5 h-5 animate-spin" /> Opening your library…</div>}

      {state === "ready" && data && (
        <div className="mt-12" data-testid="library-orders">
          <div className="flex items-center justify-between">
            <p className="text-[#4a4a4a]">Signed in as <strong data-testid="library-signed-email">{data.email}</strong></p>
            <button data-testid="library-signout" onClick={signOut} className="text-sm font-semibold text-[#0a192f] hover:text-[#d4af37] inline-flex items-center gap-1"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
          {data.orders.length === 0 && (
            <div className="card-flat p-16 text-center mt-8" data-testid="library-empty">
              <div className="font-serif text-2xl text-[#0a192f]">No purchases yet under this email.</div>
              <Link to="/shop" className="btn-gold inline-flex mt-6">Browse the shop</Link>
            </div>
          )}
          <div className="mt-8 space-y-6">
            {data.orders.map((o) => (
              <div key={o.id} className="card-flat p-6" data-testid={`library-order-${o.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="font-serif text-2xl text-[#0a192f]">{o.product_name}
                    {o.is_gift_received && <span className="ml-3 text-xs font-sans font-bold bg-[#d4af37] text-[#0a192f] px-2 py-0.5 align-middle" data-testid={`gift-badge-${o.id}`}>GIFT FROM {(o.gift_from_name || "A FRIEND").toUpperCase()}</span>}
                    {!o.is_gift_received && o.gift_to_email && <span className="ml-3 text-xs font-sans text-[#4a4a4a] align-middle" data-testid={`gifted-to-${o.id}`}>gifted to {o.gift_to_email}</span>}
                  </div>
                  <div className="text-sm text-[#4a4a4a]">{new Date(o.paid_at).toLocaleDateString()} · ${(o.amount_cents / 100).toFixed(2)}</div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="border border-[#eee] p-3">
                      <div className="flex gap-4 items-center">
                        <div className="w-14 h-18 bg-[#f4f5f7] flex-shrink-0 overflow-hidden">{it.cover_key && <img src={fileUrl(it.cover_key)} alt="" className="w-full h-full object-cover" />}</div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0a192f] text-sm leading-tight">{it.name}</div>
                          {o.kind === "physical" ? (
                            <div className="mt-2 text-xs text-[#4a4a4a]" data-testid={`library-shipping-${o.id}`}>
                              {o.fulfillment_status === "shipped" ? (
                                <>Shipped{o.carrier ? ` via ${o.carrier}` : ""}{o.tracking_number ? ` · ${o.tracking_number}` : ""} {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="underline text-[#0a192f]">Track</a>}</>
                              ) : "Being packed — we'll email you tracking"}
                            </div>
                          ) : (
                            <DownloadButtons compact baseUrl={`${API}${it.download_url.replace(/^\/api/, "")}`} item={it} primaryTestId={`library-download-${o.id}-${it.product_id}`} />
                          )}
                          <ReviewForm productId={it.product_id} productName={it.name} existing={reviews[it.product_id]} onSaved={(r) => setReviews({ ...reviews, [it.product_id]: r })} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
